import { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquare, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MarkdownEditor } from "@/components/common/markdown-editor";
import { MessageBodyMarkdown } from "@/components/messages/message-body-markdown";
import {
  createDashboardPostComment,
  deleteDashboardPostComment,
  fetchDashboardPostComments,
  updateDashboardPostComment,
} from "@/lib/api/dashboard-posts";
import { getAuthUser } from "@/lib/api/token-store";
import { ALWAYS_EXPANDED_COMMENT_COUNT } from "@/lib/dashboard/comment-threads";
import { avatarTintStyle, initialsOf } from "@/lib/dashboard/people";
import { subscribeDashboardPostEvents } from "@/lib/realtime/chat-realtime-service";
import { cn } from "@/lib/utils";

const COMMENT_MAX_LENGTH = 4000;

// The comment card's own surface and shape — shared by the composer container so an open
// composer reads as the next card in the thread rather than a panel bolted underneath it.
const THREAD_CARD = "rounded-[10px] border border-border bg-card2";

function normalizeComment(value) {
  if (!value || typeof value !== "object") return null;
  const id = String(value.id || "").trim();
  if (!id) return null;
  return {
    id,
    body: String(value.body || ""),
    authorId: String(value.authorId || "").trim(),
    authorName: String(value.authorName || "").trim(),
    createdAt: String(value.createdAt || "").trim(),
    updatedAt: String(value.updatedAt || "").trim(),
  };
}

/**
 * One comment: an initials avatar beside a card carrying the body. Edit is author-only;
 * delete is author-or-moderator — the server enforces both, this just decides which
 * affordances to draw (the same "trust the response, enforce on the server" split the post's
 * own edit/delete buttons use).
 *
 * The actions overlay the card's top-right on hover instead of taking a gutter of their own,
 * so the card keeps the full width of the thread whether or not you can act on it.
 */
function CommentRow({ comment, myUserId, canModerate, formatTime, onEdited, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [busy, setBusy] = useState(false);
  const isAuthor = Boolean(myUserId) && comment.authorId === myUserId;
  const edited = Date.parse(comment.updatedAt) - Date.parse(comment.createdAt) > 2000;

  const save = async () => {
    if (!draft.trim() || busy) return;
    setBusy(true);
    try {
      await updateDashboardPostComment(comment.postId, comment.id, { body: draft });
      setEditing(false);
      onEdited?.();
    } catch (error) {
      toast.error(error?.message || "Could not save the comment");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await deleteDashboardPostComment(comment.postId, comment.id);
      onDeleted?.();
    } catch (error) {
      toast.error(error?.message || "Could not delete the comment");
      setBusy(false);
    }
  };

  return (
    <li className="group/comment flex gap-2.5">
      <div
        className="mt-[3px] grid h-[26px] w-[26px] flex-none place-items-center rounded-full text-[10px] font-bold"
        style={avatarTintStyle(comment.authorId)}
        aria-hidden
      >
        {initialsOf(comment.authorName)}
      </div>

      <div className={cn("relative min-w-0 flex-1 px-[13px] py-2", THREAD_CARD)}>
        <p className="text-[13px] leading-snug">
          <span className="font-extrabold text-foreground">{comment.authorName || "Staff"}</span>
          <span className="text-[12px] font-normal text-muted-foreground">
            {" · "}
            {formatTime(comment.createdAt)}
            {edited ? " · edited" : ""}
          </span>
        </p>

        {editing ? (
          <div className="mt-2 space-y-2">
            <MarkdownEditor
              value={draft}
              onChange={setDraft}
              maxLength={COMMENT_MAX_LENGTH}
              disabled={busy}
              minHeightClassName="min-h-16"
              previewMaxHeightClassName="max-h-40"
              className="rounded-[10px] border-border bg-card"
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => {
                  setDraft(comment.body);
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
              <Button type="button" size="sm" disabled={busy || !draft.trim()} onClick={() => void save()}>
                {busy ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        ) : (
          <MessageBodyMarkdown className="mt-0.5 text-[13.5px] text-[color-mix(in_oklab,var(--foreground)_88%,var(--muted-foreground))]">
            {comment.body}
          </MessageBodyMarkdown>
        )}

        {!editing && (isAuthor || canModerate) && (
          <div className="absolute right-1.5 top-1.5 flex items-center gap-0.5 rounded-md bg-card2 opacity-0 transition-opacity duration-150 focus-within:opacity-100 group-hover/comment:opacity-100">
            {isAuthor && (
              <button
                type="button"
                title="Edit comment"
                disabled={busy}
                onClick={() => setEditing(true)}
                className="grid h-[24px] w-[24px] cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-accent/60 hover:text-foreground"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              type="button"
              title="Delete comment"
              disabled={busy}
              onClick={() => void remove()}
              className="grid h-[24px] w-[24px] cursor-pointer place-items-center rounded-md text-rose-600 transition-colors duration-150 hover:bg-rose-600/10 dark:text-rose-400"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * The comment thread under one announcement, hanging off a rule down its left edge.
 *
 * Mounted only while the post is expanded — the social bar above owns that decision — so this
 * is also where the fetch lives. Comments are kept in view-local state, never in the posts
 * store: they are deliberately absent from the polled feed payload, so the store has nothing
 * to say about them. While mounted, the panel listens for realtime pings and refetches only
 * when the ping names THIS post; a busy announcement elsewhere costs nothing.
 */
export function AnnouncementComments({
  postId,
  canModerate,
  formatTime,
  composerOpen,
  onOpenComposer,
  onCloseComposer,
  onCountChange,
}) {
  const [comments, setComments] = useState(null); // null = loading
  const [showAll, setShowAll] = useState(false);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const composerRef = useRef(null);
  const myUserId = String(getAuthUser()?.id || "").trim();
  // Kept in a ref so the realtime subscription never has to re-bind on a count change.
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const load = useCallback(async () => {
    try {
      const response = await fetchDashboardPostComments(postId);
      const items = response.items.map(normalizeComment).filter(Boolean);
      setComments(items);
      onCountChangeRef.current?.(items.length);
      return items;
    } catch {
      setComments([]);
      return [];
    }
  }, [postId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(
    () =>
      subscribeDashboardPostEvents((event) => {
        if (String(event?.action || "") !== "commented") return;
        if (String(event?.postId || "").trim() !== String(postId)) return;
        void load();
      }),
    [postId, load],
  );

  // Opening the composer should put the caret in it — the click was already the decision to
  // write. Focused directly rather than through rAF, which never fires in a hidden window.
  useEffect(() => {
    if (!composerOpen) return;
    composerRef.current?.querySelector("textarea")?.focus();
  }, [composerOpen]);

  const submit = async () => {
    if (!draft.trim() || posting) return;
    setPosting(true);
    try {
      await createDashboardPostComment(postId, { body: draft });
      setDraft("");
      // Your own comment always lands in view, however long the thread already is.
      setShowAll(true);
      await load();
      // The comment is now in the list; leaving the box open would just be clutter.
      onCloseComposer?.();
    } catch (error) {
      toast.error(error?.message || "Could not post the comment");
    } finally {
      setPosting(false);
    }
  };

  const closeComposer = () => {
    setDraft("");
    onCloseComposer?.();
  };

  const hidden =
    comments && !showAll ? Math.max(0, comments.length - ALWAYS_EXPANDED_COMMENT_COUNT) : 0;
  const visible = hidden > 0 ? comments.slice(-ALWAYS_EXPANDED_COMMENT_COUNT) : comments;

  return (
    <div className="mb-0.5 ml-3 mt-[13px] flex flex-col gap-2.5 border-l-2 border-border pl-[17px]">
      {comments === null ? (
        <div className="space-y-2 py-1">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      ) : (
        <>
          {hidden > 0 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="self-start text-[12.5px] font-bold text-primary transition-colors duration-150 hover:underline"
            >
              Show {hidden} earlier comment{hidden === 1 ? "" : "s"}
            </button>
          )}
          {visible.length > 0 && (
            <ul className="flex flex-col gap-2.5">
              {visible.map((comment) => (
                <CommentRow
                  key={comment.id}
                  comment={{ ...comment, postId }}
                  myUserId={myUserId}
                  canModerate={canModerate}
                  formatTime={formatTime}
                  onEdited={load}
                  onDeleted={load}
                />
              ))}
            </ul>
          )}
        </>
      )}

      {composerOpen ? (
        <div
          ref={composerRef}
          className="space-y-2"
          // Esc backs out of writing without backing out of reading — the thread stays open.
          onKeyDown={(event) => {
            if (event.key !== "Escape" || posting) return;
            event.stopPropagation();
            closeComposer();
          }}
        >
          <MarkdownEditor
            value={draft}
            onChange={setDraft}
            maxLength={COMMENT_MAX_LENGTH}
            disabled={posting}
            minHeightClassName="min-h-16"
            previewMaxHeightClassName="max-h-40"
            placeholder="Write a comment… markdown is supported."
            className="rounded-[10px] border-border bg-card"
          />
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="outline" disabled={posting} onClick={closeComposer}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={posting || !draft.trim()}
              onClick={() => void submit()}
            >
              <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
              {posting ? "Posting…" : "Comment"}
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={onOpenComposer}
          className={cn(
            "inline-flex items-center gap-[7px] self-start rounded-full px-[13px] py-1.5 text-[12.5px] font-bold text-muted-foreground transition-colors duration-150 hover:border-[color-mix(in_oklab,var(--primary)_45%,var(--border))] hover:text-primary",
            THREAD_CARD,
          )}
        >
          <MessageSquare className="h-[13px] w-[13px]" aria-hidden />
          Comment
        </button>
      )}
    </div>
  );
}
